// Floating banner shown on every customer page whenever they have an ACTIVE order.
// Solves the UX issue where customers close the site and can't find their current order.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/api/client";
import useWebSocket from "@/hooks/useWebSocket";
import { useAuth } from "@/contexts/AuthContext";
import { MapPin, ChevronLeft, Bike, ChefHat, PackageCheck, Clock } from "lucide-react";

const ACTIVE_STATUSES = new Set(["pending", "preparing", "ready", "assigned", "picked_up"]);

// Deep, saturated colors → strong contrast with white text for max readability.
const STATUS_META = {
  pending:   { label: "بانتظار تأكيد المطعم", Icon: Clock,         from: "#B45309", to: "#78350F" },
  preparing: { label: "المطعم يُحضّر طلبك",     Icon: ChefHat,       from: "#C2410C", to: "#7C2D12" },
  ready:     { label: "جاهز — بانتظار سائق",   Icon: PackageCheck,  from: "#0369A1", to: "#0C4A6E" },
  assigned:  { label: "السائق قادم للاستلام",  Icon: Bike,          from: "#7E22CE", to: "#581C87" },
  picked_up: { label: "السائق في الطريق إليك", Icon: Bike,          from: "#B91C1C", to: "#7F1D1D" },
};

export default function ActiveOrderBanner() {
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const load = async () => {
    if (!user || user.role !== "customer") return;
    try {
      const { data } = await api.get("/orders/mine");
      const active = (data || []).find((o) => ACTIVE_STATUSES.has(o.status));
      setOrder(active || null);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [user]);

  // Real-time updates when status changes
  useWebSocket({
    onMessage: (msg) => {
      if (!order) return;
      if (msg.topic === `order:${order.id}` && (msg.event === "order.status_changed" || msg.event === "order.updated")) {
        load();
      }
    },
  });

  if (!user || user.role !== "customer" || !order || dismissed) return null;

  const meta = STATUS_META[order.status] || STATUS_META.pending;
  const Icon = meta.Icon;

  return (
    <div className="sticky top-0 left-0 right-0 z-40 px-3 pt-2 pb-1" data-testid="active-order-banner">
      <Link
        to={`/track/${order.id}`}
        className="block max-w-4xl mx-auto rounded-2xl overflow-hidden talabya-shadow-float border border-white/10"
        style={{
          background: `linear-gradient(135deg, ${meta.from} 0%, ${meta.to} 100%)`,
          textShadow: "0 1px 2px rgba(0,0,0,0.25)",
        }}
      >
        <div className="flex items-center gap-3 px-4 py-3.5 text-white">
          <div className="w-12 h-12 rounded-xl bg-white/25 backdrop-blur flex items-center justify-center shrink-0 animate-pulse-dot ring-1 ring-white/30">
            <Icon size={24} strokeWidth={2.5}/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-black text-base leading-tight" style={{fontFamily:"Cairo"}}>لديك طلب نشط الآن</span>
              <span className="text-[11px] bg-white/95 text-black/85 px-2 py-0.5 rounded-full font-mono font-black tracking-wider" style={{textShadow:"none"}} data-testid="active-order-code">
                {order.code}
              </span>
            </div>
            <div className="text-[13px] text-white font-bold truncate mt-0.5">{meta.label}</div>
          </div>
          <div className="flex items-center gap-1 shrink-0 text-xs font-black bg-white/15 px-2.5 py-1.5 rounded-lg">
            <MapPin size={14}/> تتبع
            <ChevronLeft size={16}/>
          </div>
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDismissed(true); }}
            className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/35 flex items-center justify-center text-white text-base font-bold shrink-0"
            aria-label="إخفاء"
            data-testid="active-banner-dismiss"
          >
            ×
          </button>
        </div>
      </Link>
    </div>
  );
}
